import type { TypedSupabaseClient } from '../client';
import { resolveStorageImageUrl } from './storage-image';

// ------------------------------------------------------------------
// Pro order/quote line → article snapshot resolver (shared)
//
// Pro commande/devis lines persist only the `article_id` — no name/code
// snapshot. The admin & mobile detail screens used to resolve the name from
// the client-side catalog cache (`useArticles`, capped at 1000 rows). That
// breaks two ways:
//   1. large-catalog tenants (> 1000 articles) — uncached articles fall back
//      to the raw UUID;
//   2. deleted articles — never in the cache, also fall back to the UUID.
//
// This resolves the identity **by id, server-side** (`.in('id', ids)`, no
// cap) so every line that still references a live article gets a real name —
// regardless of catalog size. Ids with no matching row (deleted articles) are
// simply absent from the map, and the UI shows a "deleted article" label
// instead of the UUID.
// ------------------------------------------------------------------

export interface ProArticleSnapshot {
  name: string;
  /** ProSpace reference: `pro_article_pricing.reference` when set, else `articles.code`. */
  code: string;
  imageUrl: string | null;
}

export async function fetchProArticleSnapshots(
  supabase: TypedSupabaseClient,
  articleIds: ReadonlyArray<string | null | undefined>,
): Promise<Map<string, ProArticleSnapshot>> {
  const ids = [...new Set(articleIds.filter((id): id is string => !!id))];
  if (ids.length === 0) return new Map();

  // The embedded `pro_article_pricing` row (FK article_id → articles.id, one
  // row per article) carries the ProSpace-only title and reference. These
  // lines belong to the Pro channel, so they must read the same labels the
  // electrician saw in the catalog — otherwise a product renamed (or
  // re-referenced) for ProSpace would show its dashboard designation on the
  // quote it ends up on.
  const { data, error } = await (supabase as any)
    .from('authorized_articles')
    .select('id, name, code, image_url, images, tenant_id, pro_article_pricing(name_fr, reference)')
    .in('id', ids);
  if (error) throw error;

  const map = new Map<string, ProArticleSnapshot>();
  for (const row of (data ?? []) as Array<Record<string, unknown>>) {
    const tenantId = (row['tenant_id'] as string | null) ?? null;
    const firstImage = (row['images'] as string[] | null)?.[0] ?? null;
    const rawImage = (row['image_url'] as string | null) ?? firstImage;
    // PostgREST returns a to-one embed as an object, but older versions (and
    // views) hand back a one-element array — accept both.
    const embedded = row['pro_article_pricing'];
    const proRow = (Array.isArray(embedded) ? embedded[0] : embedded) as
      | { name_fr?: string | null; reference?: string | null }
      | null
      | undefined;
    const proName = proRow?.name_fr?.trim();
    const proReference = proRow?.reference?.trim();
    map.set(row['id'] as string, {
      name: proName || ((row['name'] as string) ?? ''),
      code: proReference || ((row['code'] as string) ?? ''),
      imageUrl: resolveStorageImageUrl(supabase, rawImage, { tenantId, folder: 'articles' }),
    });
  }
  return map;
}
